import { resolveId } from '@medplum/core'
import type {
  CodeableConcept,
  Coding,
  Organization,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { CHIP_COLORS, type ChipColor } from '#src/chipColors.ts'
import {
  LABEL_COLOR_SYSTEM,
  LABEL_IDENTIFIER_SYSTEM,
  LABEL_STATUS_TAG_SYSTEM,
} from '#src/codeSystems.ts'
import { type DateString, today } from '#src/dates.ts'
import { Extension, Tag } from '#src/decorators.ts'
import type { ExtendedResourceConstructor } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import { ExtendedBasic } from '#src/models/basic.ts'

export abstract class Label extends ExtendedBasic {
  constructor({
    code,
    name,
    color,
    author,
  }: {
    code: CodeableConcept
    name: string
    color: string
    author: Reference<Practitioner | Organization>
  }) {
    super({
      code,
      created: today(), // Basic requires a date for created instead of a datetime.
      author,
    })
    this.name = name
    this.color = color
    this._status = Label.status.active
  }

  get name(): string {
    const name = this.resource.identifier?.[0]?.value
    invariant(name, 'Missing name')
    return name
  }

  set name(value: string) {
    this.resource.identifier = [{ system: LABEL_IDENTIFIER_SYSTEM, value }]
  }

  @Extension({
    url: LABEL_COLOR_SYSTEM,
    kind: 'valueString',
  })
  color: string

  static status = {
    active: {
      system: LABEL_STATUS_TAG_SYSTEM,
      code: 'active',
    } satisfies Coding,
    enteredInError: {
      system: LABEL_STATUS_TAG_SYSTEM,
      code: 'entered-in-error',
    } satisfies Coding,
  }

  @Tag({
    system: LABEL_STATUS_TAG_SYSTEM,
  })
  _status: Coding

  delete(): void {
    this._status = Label.status.enteredInError
  }

  get isDeleted(): boolean {
    return this._status.code === Label.status.enteredInError.code
  }

  get created(): DateString {
    const created = this.resource.created
    invariant(created, 'Missing created')
    return created
  }

  /** The color-picker options for labels, sourced from the shared palette. */
  static defaultColors: ChipColor[] = CHIP_COLORS

  protected getExportFields() {
    return {
      id: this.id,
      name: this.name,
      color: this.color,
      created: this.created,
      isDeleted: this.isDeleted,
      authorId: resolveId(this.resource.author),
    }
  }
}

export type LabelConstructor<T extends Label> =
  ExtendedResourceConstructor<T> & {
    new (args: {
      name: string
      color: string
      author: Reference<Practitioner | Organization>
    }): T
    defaultColors: typeof Label.defaultColors
  }
